import React, { useEffect, useState } from 'react';
import {
  useRecordContext,
  useGetList,
  useNotify,
  useDataProvider,
  Loading,
  useRefresh,
} from 'react-admin';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Alert,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';

/**
 * Role Permissions Tab Component
 * 
 * Displays and manages permissions for a role.
 * All users with this role will inherit these permissions.
 */
const RolePermissionsTab = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const [rolePermissions, setRolePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Get all available permissions
  const { data: allPermissions, isLoading: permissionsLoading } = useGetList(
    'permissions',
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'ASC' },
    }
  );

  // Fetch role permissions
  useEffect(() => {
    if (!record?.id) return;

    const fetchRolePermissions = async () => {
      try {
        setLoading(true);
        const response = await dataProvider.getList('roles', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'id', order: 'ASC' },
          filter: { id: record.id },
          meta: { subResource: 'permissions' },
        });

        const permissions = response.data || [];
        setRolePermissions(permissions);
        
        // Initialize selected permissions
        const permissionIds = new Set(permissions.map(p => p.id));
        setSelectedPermissions(permissionIds);
        setHasChanges(false);
      } catch (error) {
        console.error('Failed to fetch role permissions:', error);
        notify('Failed to load role permissions', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchRolePermissions();
  }, [record?.id, dataProvider, notify]);

  const handlePermissionToggle = (permissionId) => {
    const newSelected = new Set(selectedPermissions);
    
    if (newSelected.has(permissionId)) {
      newSelected.delete(permissionId);
    } else {
      newSelected.add(permissionId);
    }
    
    setSelectedPermissions(newSelected);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!record?.id) return;

    try {
      setSaving(true);
      
      // Convert Set to Array
      const permissionIds = Array.from(selectedPermissions);

      await dataProvider.update('roles', {
        id: record.id,
        data: { permission_ids: permissionIds },
        previousData: record,
        meta: { subResource: 'permissions' },
      });

      setHasChanges(false);
      notify('Role permissions updated successfully', { type: 'success' });
      
      // Refresh to ensure UI is in sync
      refresh();
    } catch (error) {
      console.error('Failed to update permissions:', error);
      notify(error?.message || 'Failed to update role permissions', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const permissionIds = new Set(rolePermissions.map(p => p.id));
    setSelectedPermissions(permissionIds);
    setHasChanges(false);
  };

  if (!record) {
    return null;
  }

  if (loading || permissionsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Loading />
      </Box>
    );
  }

  // Group permissions by category (prefix before colon)
  const permissionsByCategory = allPermissions?.reduce((acc, permission) => {
    const [category] = permission.name.split(':');
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(permission);
    return acc;
  }, {}) || {};

  const categories = Object.keys(permissionsByCategory).sort();

  return (
    <Box p={2}>
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          Role Permissions
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          These permissions apply to all users with the <strong>{record.name || 'N/A'}</strong> role.
          Individual users can have additional permissions via the Permissions tab in User Edit.
        </Typography>
        
        {hasChanges && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have unsaved changes. Click "Save Permissions" to apply them.
          </Alert>
        )}

        <Box display="flex" gap={2} mb={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save Permissions'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={!hasChanges || saving}
          >
            Reset
          </Button>

          <Chip 
            label={`${selectedPermissions.size} permissions selected`} 
            color="primary" 
            variant="outlined"
          />
        </Box>
      </Box>

      {categories.map((category) => (
        <Paper key={category} elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textTransform: 'capitalize' }}>
            {category.replace(/-/g, ' ')}
          </Typography>
          
          <FormGroup>
            {permissionsByCategory[category].map((permission) => (
              <FormControlLabel
                key={permission.id}
                control={
                  <Checkbox
                    checked={selectedPermissions.has(permission.id)}
                    onChange={() => handlePermissionToggle(permission.id)}
                    disabled={saving}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" component="span">
                      {permission.name}
                    </Typography>
                    {permission.description && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {permission.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </Paper>
      ))}
    </Box>
  );
};

export default RolePermissionsTab;

